import { Ihashtag } from '@typings/common/hashtag.interface';
import { IinventoryItem, IstoreItem } from '@typings/store/storeItem.interface';

export const STORE_NO_CAMP_ID = '__NO_CAMP__';

export const normalizeStoreCampId = (campId?: string | null) =>
  campId && campId.trim().length > 0 ? campId : STORE_NO_CAMP_ID;

export const isCampAllowed = (
  allowedCampIds?: string[] | null,
  campId?: string | null,
) => {
  if (!Array.isArray(allowedCampIds) || allowedCampIds.length === 0) {
    return true;
  }

  return allowedCampIds.includes(normalizeStoreCampId(campId));
};

export const getHashtagMap = (hashtagList?: Ihashtag[] | null) => {
  const map = new Map<string, Ihashtag>();
  (hashtagList || []).forEach((tag) => {
    if (tag?.id) map.set(tag.id, tag);
  });
  return map;
};

export const canViewStoreCategory = (
  category: Ihashtag,
  campId?: string | null,
) => isCampAllowed(category.visibleCampIds, campId);

export const canViewStoreItem = (
  item: IstoreItem,
  campId?: string | null,
  categoryMap?: Map<string, Ihashtag>,
) => {
  if (!item?.isShow) return false;
  if (!isCampAllowed(item.visibleCampIds, campId)) return false;

  const categoryIds = item.hashtagIdList || [];
  if (!categoryMap || categoryIds.length === 0) return true;

  const relatedCategories = categoryIds
    .map((id) => categoryMap.get(id))
    .filter(Boolean) as Ihashtag[];

  if (relatedCategories.length === 0) return true;

  return relatedCategories.some((category) =>
    canViewStoreCategory(category, campId),
  );
};

export const canPurchaseStoreItemByCamp = (
  item: IstoreItem,
  campId?: string | null,
) => isCampAllowed(item.purchasableCampIds, campId);

export const canGiftStoreItem = (item?: Pick<IstoreItem, 'isGiftable'> | null) =>
  item?.isGiftable ?? true;

export const isReceivedGiftInventoryItem = (
  item?: Pick<IinventoryItem, 'isPresent' | 'giftingCharID'> | null,
) => !!item?.isPresent || !!item?.giftingCharID;

export const canGiftInventoryItem = (
  inventoryItem?: Pick<
    IinventoryItem,
    'isPresent' | 'giftingCharID' | 'isGiftable'
  > | null,
  storeItem?: Pick<IstoreItem, 'isGiftable'> | null,
) =>
  !isReceivedGiftInventoryItem(inventoryItem) &&
  canGiftStoreItem(storeItem || inventoryItem);

export const canResellStoreItem = (
  item?: Pick<IstoreItem, 'isResellable'> | null,
) => item?.isResellable ?? true;

export const getStorePurchaseLimitMessage = (
  item: IstoreItem,
  amount: number,
) => {
  const limit = item.purchaseLimit;
  if (!limit) return '';

  const total = Number(limit.total || 0);
  const totalPurchased = Number(limit.totalPurchased || 0);
  if (total > 0 && totalPurchased + amount > total) {
    return `전체 판매 수량이 부족합니다. 남은 수량: ${Math.max(
      0,
      total - totalPurchased,
    )}`;
  }

  return '';
};

export const getStoreStockStatus = (item: IstoreItem) => {
  const total = Number(item?.purchaseLimit?.total || 0);
  if (total <= 0) return null;

  const totalPurchased = Number(item?.purchaseLimit?.totalPurchased || 0);
  const remaining = Math.max(0, total - totalPurchased);

  return {
    remaining,
    total,
    isSoldOut: remaining <= 0,
  };
};
